import React from 'react';
import { ComplianceCompanyConfig } from '@/constants/compliance-companies';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AlertCircle, Phone, ArrowLeft, BookOpen, Target } from 'lucide-react';

interface ComplianceOverviewProps {
  config: ComplianceCompanyConfig;
  onBeginCall: () => void;
  onBack: () => void;
}

export const ComplianceOverview: React.FC<ComplianceOverviewProps> = ({ config, onBeginCall, onBack }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <img src={config.logo} alt={config.name} className="h-10" />
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Powered by</span>
            <img src={config.hupoLogo} alt="Hupo" className="h-6" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Pre-Call Briefing</h1>
          <p className="text-gray-600">Review the scenario details and compliance considerations before starting</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Scenario Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
                Scenario Background
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  You're about to receive a call from <strong>{config.scenario.participants.client.name}</strong>,
                  the {config.scenario.participants.client.role} of your client company.
                </p>
                <p>
                  The company is in the process of acquiring a Taiwanese industrial supplier, and you've been
                  working as their investment banking advisor for several weeks. This is a routine catch-up call,
                  but as you'll discover, it contains several compliance challenges.
                </p>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">Your Objective</h3>
                  <p className="text-sm">
                    Navigate the conversation professionally while identifying and appropriately handling
                    compliance issues as they arise. Remember to balance client service with regulatory requirements.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-amber-600" />
                Key Compliance Areas to Watch
              </h2>
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Cross-Border Licensing</p>
                    <p className="text-sm text-gray-600">
                      Be aware of restrictions on conducting regulated activities outside your licensed jurisdiction
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Material Non-Public Information (MNPI)</p>
                    <p className="text-sm text-gray-600">
                      Identify when clients share information that could be considered MNPI and respond appropriately
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Communication Protocols</p>
                    <p className="text-sm text-gray-600">
                      Maintain proper communication channels with audit trails and avoid unofficial platforms
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column - Call Setup */}
          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Participants</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">You are:</p>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="font-semibold text-gray-900">{config.scenario.participants.user.name}</p>
                    <p className="text-sm text-gray-600">{config.scenario.participants.user.role}</p>
                    <p className="text-xs text-gray-500">{config.scenario.participants.user.location}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Calling you:</p>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="font-semibold text-gray-900">{config.scenario.participants.client.name}</p>
                    <p className="text-sm text-gray-600">{config.scenario.participants.client.role}</p>
                    <p className="text-xs text-gray-500">{config.scenario.participants.client.company}</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Target className="w-5 h-5 mr-2" />
                Remember
              </h2>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  Stay professional and helpful
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  Identify compliance issues as they arise
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  Respond appropriately without damaging the relationship
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  Document any concerns for follow-up
                </li>
              </ul>
            </Card>

            <Button
              onClick={onBeginCall}
              className="w-full py-4 text-lg font-semibold flex items-center justify-center gap-2"
              style={{
                backgroundColor: config.colors.primary,
                color: 'white'
              }}
            >
              <Phone className="w-5 h-5" />
              Begin Call
            </Button>

            <p className="text-xs text-gray-500 text-center">
              The call will last approximately {config.scenario.duration}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};